import { autorun, makeAutoObservable, runInAction } from "mobx";
import { ChangeEvent } from "react";
import { isEqual, sortBy, sumBy } from "lodash";
import { DecisionType } from "@/common/decisions";
import { LocalizedText } from "@/common/common";
import {
  BooleanFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import {
  CleanupFunction,
  DecisionContext,
  SharesForShareholder,
  shareholdersWithShares,
} from "@/dbot/common/commonDomain";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { ShareholderDTO } from "@/types/Shareholder";
import { ActorType, ActorsSelectorModel, SelectedActor } from "@/types/SelectActors";
import { mapToActors } from "@/shareholderRegister/transactions/shareholderActorUtils";
import { resolveUrnReference, toShareholderReference } from "@/utils/UrnReference";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";

export enum ShareTransferConsentCondition {
  shareholdersAgreement = "SHAREHOLDERS_AGREEMENT",
  other = "OTHER",
}

const conditionLabels: Record<ShareTransferConsentCondition, TMessageKey> = {
  [ShareTransferConsentCondition.shareholdersAgreement]:
    "decisions.shareTransferConsent.conditions.SHAREHOLDERS_AGREEMENT",
  [ShareTransferConsentCondition.other]: "decisions.shareTransferConsent.conditions.OTHER",
};

export type ShareTransferConsent = {
  type: DecisionType.shareTransferConsent;
  transferorShareholderId?: number;
  transferorName?: string;
  numberOfShares?: number;
  acquirerName?: string;
  acquirerShareholderId?: number;
  pricePerShare?: number;
  isConsentGiven?: boolean;
  conditions: ShareTransferConsentCondition[];
  otherCondition: LocalizedText[];
  refusalGrounds: LocalizedText[];
};

export class ShareTransferConsentForm {
  readonly type = DecisionType.shareTransferConsent;
  readonly context: DecisionContext<ShareTransferConsent>;
  transferor: RadioFieldModel<ShareholderDTO | null>;
  numberOfShares: NumberFieldModel;
  acquirerName: StringFieldModel;
  acquirerShareholderId?: number;
  pricePerShare: NumberFieldModel;
  isConsentGiven: BooleanFieldModel;
  conditions: OptionFieldModel<ShareTransferConsentCondition>;
  otherCondition: LocalizedTextEditorModel;
  refusalGrounds: LocalizedTextEditorModel;
  acquirerDialog?: ActorsSelectorModel = undefined;
  private readonly data?: ShareTransferConsent;

  constructor(context: DecisionContext<ShareTransferConsent>, data?: ShareTransferConsent) {
    this.context = context;
    this.data = data;
    this.transferor = new RadioFieldModel<ShareholderDTO | null>({
      value: this.shareholders.find((s) => s.id === data?.transferorShareholderId) ?? null,
      options: () => this.shareholders,
      idFn: (s) => (s ? s.id.toString() : ""),
      labelFn: (s) => ({ translated: s?.name ?? "" }),
      required: true,
      onChange: () => this.revalidateNumberOfShares(),
    });
    this.numberOfShares = new NumberFieldModel({
      value: data?.numberOfShares,
      required: true,
      isInteger: true,
      isPositive: true,
      validator: (value) =>
        value !== undefined && value > this.transferorNumberOfShares
          ? {
              key: "number-field.smaller-or-equal-than-n",
              params: { n: { type: "number", value: this.transferorNumberOfShares } },
            }
          : undefined,
    });
    this.acquirerName = new StringFieldModel({ value: data?.acquirerName, required: true });
    this.acquirerShareholderId = data?.acquirerShareholderId;
    this.pricePerShare = new NumberFieldModel({ value: data?.pricePerShare, isPositive: true });
    this.isConsentGiven = new BooleanFieldModel({ value: data?.isConsentGiven, required: true });
    this.conditions = new OptionFieldModel<ShareTransferConsentCondition>({
      value: data?.conditions ?? [],
      options: Object.values(ShareTransferConsentCondition),
      idFn: (c) => c,
      labelFn: (c) => ({ key: conditionLabels[c] }),
    });
    this.otherCondition = this.createTextEditor(
      { key: "decisions.shareTransferConsent.otherCondition.title" },
      data?.otherCondition,
    );
    this.refusalGrounds = this.createTextEditor(
      { key: "decisions.shareTransferConsent.refusalGrounds.title" },
      data?.refusalGrounds,
    );
    makeAutoObservable(this, { type: false, context: false }, { autoBind: true });
  }

  private createTextEditor(label: TMessage, texts?: LocalizedText[]): LocalizedTextEditorModel {
    const editor = new LocalizedTextEditorModel({
      texts,
      label,
      aiClient: this.context.aiClient,
      isReadOnly: () => this.isProjectLocked,
      required: true,
    });
    editor.onLanguageChange(this.context.documentLanguages, texts);
    return editor;
  }

  init(): CleanupFunction {
    return autorun(() => {
      this.syncTextLanguages(this.otherCondition, this.data?.otherCondition);
      this.syncTextLanguages(this.refusalGrounds, this.data?.refusalGrounds);
    });
  }

  private syncTextLanguages(editor: LocalizedTextEditorModel, texts?: LocalizedText[]) {
    const languages = this.context.documentLanguages;
    const current = editor.textModels.map((t) => t.locale);
    if (!isEqual(sortBy(languages), sortBy(current))) {
      runInAction(() => editor.onLanguageChange(languages, texts));
    }
  }

  private get sharesForShareholders(): SharesForShareholder[] {
    return (
      this.context.inputState?.sharesForShareholders ??
      this.context.initialState.sharesForShareholders
    );
  }

  get shareholders(): ShareholderDTO[] {
    return shareholdersWithShares(
      this.context.initialState.shareholders,
      this.sharesForShareholders,
    );
  }

  private revalidateNumberOfShares() {
    if (this.numberOfShares.parsedValue !== undefined) {
      this.numberOfShares.validate();
    }
  }

  get transferorNumberOfShares(): number {
    const transferorId = this.transferor.value?.id;
    return sumBy(
      this.sharesForShareholders.filter((s) => s.shareholderId === transferorId),
      (s) => s.numberOfShares,
    );
  }

  get totalNumberOfShares(): number {
    return sumBy(this.sharesForShareholders, (s) => s.numberOfShares);
  }

  get isAllSharesInCompany(): boolean {
    const value = this.numberOfShares.parsedValue;
    return value !== undefined && value > 0 && value === this.totalNumberOfShares;
  }

  get isRefused(): boolean {
    return this.isConsentGiven.value === false;
  }

  get isOtherConditionSelected(): boolean {
    return (
      this.isConsentGiven.value === true &&
      this.conditions.isSelected(ShareTransferConsentCondition.other)
    );
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get acquirerCandidates(): ShareholderDTO[] {
    return this.context.initialState.shareholders.filter((s) => !s.isUnidentified);
  }

  openAcquirerDialog() {
    this.acquirerDialog = new ActorsSelectorModel({
      title: { key: "decisions.shareTransferConsent.acquirer.dialog.title" },
      organisationNumber: this.context.orgNo,
      tabs: [ActorType.SHAREHOLDER],
      excludeActors: this.transferor.value
        ? [toShareholderReference(this.transferor.value.id)]
        : [],
      isMultiSelect: false,
      onLoad: (_, onSuccess) => {
        onSuccess(mapToActors(this.acquirerCandidates));
      },
      onSubmit: (selected) => this.selectAcquirer(selected[0]),
      onClose: this.closeAcquirerDialog,
    });
  }

  closeAcquirerDialog() {
    this.acquirerDialog = undefined;
  }

  selectAcquirer(actor: SelectedActor | undefined) {
    this.closeAcquirerDialog();
    if (!actor) {
      return;
    }
    this.acquirerName.setAndCommitInputValue(actor.name);
    this.acquirerShareholderId = existingShareholderId(actor, this.context.orgNo);
  }

  typeAcquirerName(input: string | ChangeEvent<HTMLInputElement>) {
    this.acquirerName.setInputValue(input);
    this.acquirerShareholderId = undefined;
  }

  validate(): boolean {
    this.transferor.validate();
    this.numberOfShares.validate();
    this.acquirerName.validate();
    this.pricePerShare.validate();
    this.isConsentGiven.validate();
    this.requiredTextEditors.forEach((e) => e.validate());
    return this.isValid;
  }

  private get requiredTextEditors(): LocalizedTextEditorModel[] {
    if (this.isOtherConditionSelected) {
      return [this.otherCondition];
    }
    if (this.isRefused) {
      return [this.refusalGrounds];
    }
    return [];
  }

  get isValid(): boolean {
    return (
      this.transferor.isValid &&
      this.transferor.value !== null &&
      this.numberOfShares.isValid &&
      this.acquirerName.isValid &&
      this.pricePerShare.isValid &&
      this.isConsentGiven.isValid &&
      this.isConsentGiven.value !== undefined &&
      this.requiredTextEditors.every(
        (e) => e.isValid && e.textModels.every((t) => !t.model.isEmpty),
      )
    );
  }

  toData(): ShareTransferConsent {
    const isConsentGiven = this.isConsentGiven.value;
    return {
      type: DecisionType.shareTransferConsent,
      transferorShareholderId: this.transferor.value?.id,
      transferorName: this.transferor.value?.name,
      numberOfShares: this.numberOfShares.parsedValue,
      acquirerName: this.acquirerName.validatedValue,
      acquirerShareholderId: this.acquirerShareholderId,
      pricePerShare: this.pricePerShare.parsedValue,
      isConsentGiven,
      conditions: isConsentGiven ? this.conditions.selected : [],
      otherCondition: this.isOtherConditionSelected ? this.otherCondition.localizedTexts : [],
      refusalGrounds: this.isRefused ? this.refusalGrounds.localizedTexts : [],
    };
  }
}

export function existingShareholderId(actor: SelectedActor, orgNo: string): number | undefined {
  const reference = resolveUrnReference(actor.reference);
  return reference?.type === "shareholderId" && actor.organisationNumber === orgNo
    ? reference.id
    : undefined;
}
